import { useState, useEffect } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Typography,
  Box,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import {
  KeyboardArrowRightOutlined,
  KeyboardArrowDownOutlined,
  AnchorOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import {
  getLpSuppliers,
  getPorts,
  getCountries,
  getLpSupplierPortMapping,
  addLpSupplierPortMapping,
  delLpSupplierPortMapping,
} from '../../../../features/master-data';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';

const CustomTreeItem = styled(TreeItem)({
  [`& .${treeItemClasses.iconContainer}`]: {
    '& .close': {
      opacity: 0.3,
    },
  },
});

const SupplierPortMappingTab = () => {
  const [ports, setPorts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [portDirection, setPortDirection] = useState('Departure');
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [initialMappings, setInitialMappings] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        params.append('isPortOperator', true);
        const [portsResponse, countriesResponse, lpSupplierResponse] =
          await Promise.all([
            getPorts(),
            getCountries(),
            getLpSuppliers(params),
          ]);
        setPorts(portsResponse);
        setCountries(countriesResponse);
        setSuppliers(lpSupplierResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error fetching data.',
          severity: 'error',
        });
      }
    };

    fetchData();
  }, []);

  const handleChangePortDirection = async (event, newMode) => {
    setPortDirection(newMode);
    if (selectedSupplier) {
      await fetchMappings(selectedSupplier.supplierNo, newMode);
    }
  };

  const handleSupplierChange = async (event, newValue) => {
    setSelectedSupplier(newValue);
    if (newValue) {
      await fetchMappings(newValue.supplierNo, portDirection);
    } else {
      setSelectedPorts([]);
      setInitialMappings([]);
    }
  };

  const fetchMappings = async (supplierNo, direction) => {
    try {
      const mappingData = await getLpSupplierPortMapping(supplierNo);
      const filteredMappings = mappingData.filter(
        (mapping) => mapping.portType === direction.toUpperCase()
      );
      const selectedPortIds = filteredMappings.map(
        (mapping) => mapping.port.unLocode
      );
      setSelectedPorts(selectedPortIds);
      setInitialMappings(filteredMappings);
    } catch (error) {
      console.error('Error fetching Mappings:', error);
      setSelectedPorts([]);
      setInitialMappings([]);
    }
  };

  const handlePortSelection = (event, nodeIds) => {
    setSelectedPorts(nodeIds);
  };

  const handleSubmit = async () => {
    if (!selectedSupplier || selectedPorts.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select a supplier and at least one port.',
        severity: 'warning',
      });
      return;
    }

    const mappingsToAdd = selectedPorts
      .filter(
        (portId) =>
          !initialMappings.some((mapping) => mapping.port.unLocode === portId)
      )
      .map((portId) => ({
        supplier: { supplierNo: selectedSupplier.supplierNo },
        port: { unLocode: portId },
        portType: portDirection.toUpperCase(),
      }));

    const mappingsToDelete = initialMappings.filter(
      (mapping) => !selectedPorts.includes(mapping.port.unLocode)
    );

    try {
      await Promise.all(
        mappingsToAdd.map((mapping) => addLpSupplierPortMapping(mapping))
      );
      await Promise.all(
        mappingsToDelete.map((mapping) => delLpSupplierPortMapping(mapping.id))
      );
      setSnackbar({
        open: true,
        message: 'Mappings updated successfully!',
        severity: 'success',
      });
      await fetchMappings(selectedSupplier.supplierNo, portDirection);
    } catch (error) {
      console.error('Error updating mappings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update mappings.',
        severity: 'error',
      });
    }
  };

  const organizeData = (ports, countries) => {
    const data = {};

    countries.forEach((country) => {
      data[country.countryCode] = {
        countryName: country.name,
        ports: [],
      };
    });

    ports.forEach((port) => {
      const { country } = port;
      if (data[country.countryCode]) {
        data[country.countryCode].ports.push(port);
      }
    });

    return data;
  };

  const treeData = organizeData(ports, countries);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Typography marginLeft={1} variant='h3'>
        Supplier Port Mapping
      </Typography>
      <Autocomplete
        sx={{ width: 200, margin: 1 }}
        options={suppliers}
        getOptionLabel={(option) => option.supplierName}
        onChange={handleSupplierChange}
        renderInput={(params) => (
          <TextField {...params} label='Supplier Name' />
        )}
      />

      <ToggleButtonGroup
        color='primary'
        value={portDirection}
        exclusive
        onChange={handleChangePortDirection}
        aria-label='Port'
      >
        <ToggleButton value='Departure'>Departure</ToggleButton>
        <ToggleButton value='Destination'>Destination</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ maxHeight: '30vh', overflowY: 'auto', maxWidth: '250px' }}>
        <SimpleTreeView
          multiSelect
          selectedItems={selectedPorts}
          onSelectedItemsChange={handlePortSelection}
          checkboxSelection
          slots={{
            expandIcon: KeyboardArrowRightOutlined,
            collapseIcon: KeyboardArrowDownOutlined,
            endIcon: AnchorOutlined,
          }}
        >
          {Object.entries(treeData).map(
            ([countryCode, { countryName, ports }]) => (
              <CustomTreeItem
                key={countryCode}
                itemId={countryCode}
                label={countryName}
              >
                {ports.map((port) => (
                  <CustomTreeItem
                    key={port.unLocode}
                    itemId={port.unLocode}
                    label={`${port.unLocode}  ${port.name}`}
                  />
                ))}
              </CustomTreeItem>
            )
          )}
        </SimpleTreeView>
      </Box>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        disabled={!selectedSupplier || selectedPorts.length === 0}
      >
        Create Mappings
      </Button>

      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default SupplierPortMappingTab;
